/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { SubscriptionGate } from './SubscriptionGate';
import { isRunningStatus } from './inactive/subscription-lock';
import { SubscriptionActivationScreen } from './SubscriptionActivationScreen';
import { useSubscriptionActivation } from '../hooks/useSubscriptionActivation';
import type { AgencyModelReturnFindById } from '@services/requests/housien-models/AgencyModelReturnFindById';
import type { AgencyStatus } from '@/features/onboarding/types/agency-dashboard';

interface ActivationGateAgency {
  id?: string;
  effective_status?: AgencyStatus | null;
}

interface SubscriptionActivationGateProps {
  agency: ActivationGateAgency;
  onActivated?: (agency: AgencyModelReturnFindById) => void;
  children: ReactNode;
}

/**
 * Bridges the post-checkout race window. When the URL carries `step=activation`
 * and the subscription is not yet running, it polls the reconciliation endpoint and
 * shows a "Confirmando..." screen instead of the misleading blocked overlay.
 * Otherwise it defers entirely to the regular SubscriptionGate.
 */
export function SubscriptionActivationGate({ agency, onActivated, children }: SubscriptionActivationGateProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isActivating = searchParams.get('step') === 'activation';
  const alreadyActive = isRunningStatus(agency.effective_status);
  const shouldPoll = isActivating && !alreadyActive;

  const { status, agency: reconciled, retry } = useSubscriptionActivation({
    agencyId: agency.id ?? '',
    enabled: shouldPoll,
  });

  useEffect(() => {
    if (!isActivating) return;
    if (status === 'SUCCESS' && reconciled) {
      onActivated?.(reconciled);
    }
    if (alreadyActive || status === 'SUCCESS') {
      const next = new URLSearchParams(searchParams);
      next.delete('step');
      setSearchParams(next, { replace: true });
    }
  }, [isActivating, alreadyActive, status, reconciled, onActivated, searchParams, setSearchParams]);

  if (shouldPoll && status !== 'SUCCESS') {
    return <SubscriptionActivationScreen status={status} agencyId={agency.id ?? ''} onRetry={retry} />;
  }

  const effectiveAgency = status === 'SUCCESS' && reconciled ? reconciled : agency;

  return <SubscriptionGate agency={effectiveAgency}>{children}</SubscriptionGate>;
}
